import React, { FC, useMemo } from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import Typography from 'components/commercetools-ui/atoms/typography';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import Wrapper from 'components/HOC/wrapper';
import Slider from 'components/commercetools-ui/atoms/slider';
import Subtitle from 'components/commercetools-ui/atoms/subtitle';
import Link from 'components/commercetools-ui/atoms/link';
import Image from 'frontastic/lib/image';
import usePreloadImages from 'helpers/hooks/usePreloadImages';

import { GenericSectionContent, MediaCard } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

const OrionSlide: FC<{ card: MediaCard }> = ({ card }) => {
  const image = card?.desktop_image;
  const cardTitle = card?.title;
  const cardDescription = card?.description;
  const ctas = card?.ctas;
  const firstCta = ctas?.[0];

  const height = 'flex gap-1.5 flex-col';

  const description = cardDescription
    ? `mt-12 font-normal leading-6 text-wrap text-16 xl:text-14 2xl:text-16 text-[#333] min-h-[50px] ${height}`
    : '';

  const link = `w-full overflow-hidden relative ${cardDescription ? 'h-auto' : 'h-auto'} h-auto hover:bg-[#F3EDE9]`;
  usePreloadImages(image?.url ? [image.url] : []);

  return (
    <Link target={firstCta?.target ?? '_self'} link={firstCta?.url} className={`${link} group`}>
      {image && (
        <div className="relative">
          <div className="absolute z-10 bg-black opacity-20"></div>
          <Image
            priority
            loading="eager"
            src={encodeURI(image?.url)}
            alt={image?.description ?? ''}
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto',
              maxWidth: '650px',
              objectFit: 'cover',
            }}
            width={500}
            height={300}
          />
        </div>
      )}

      {/* Remove "overflow-hidden" from css because we want to show full name */}
      <div className="px-[20px] pb-[10px]">
        {cardTitle && (
          <Typography
            as="h4"
            className="mt-12 max-w-[90%] whitespace-pre-line break-words text-22 font-medium leading-[30px] text-[#090909] md:text-18 lg:text-20 xl:text-18 2xl:text-22"
          >
            {cardTitle}
          </Typography>
        )}
        {cardDescription && (
          <div
            className={`${description}`}
            dangerouslySetInnerHTML={{ __html: sanitize(cardDescription) }}
          />
        )}
        {ctas && ctas.length > 0 && (
          <div className={`${height} hidden group-hover:translate-x-14 md:block`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="green">
              <path
                d="M18 9L25 16M25 16L18 23M25 16L7 16"
                stroke="#666666"
                className="hidden group-hover:stroke-[#090909] md:block"
                strokeWidth="2"
                strokeLinecap="square"
              />
            </svg>
          </div>
        )}
      </div>
    </Link>
  );
};

export default function OrionRendering({ content }: { content: GenericSectionContent }) {
  const { cards, title, description, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'OrionRendering');

  const slideCount = cards?.length || 0;

  const [windowWidth] = useMediaQuery();
  const showSliderWidth = slideCount === 2 ? 642 : 1024;
  const showSlider = windowWidth <= showSliderWidth;
  const { isTouchDevice } = useTouchDevice();

  let overrideContainerStyle = {};
  let overrideSlideGap = '';
  if (slideCount === 2) {
    const maxWidth = showSlider || windowWidth > 1500 || windowWidth < 800 ? 1300 : windowWidth - 280;
    overrideContainerStyle = {
      maxWidth: `${maxWidth}px`,
    };
    overrideSlideGap = "md:gap-24 xl:gap-48 2xl:gap-64 3xl:gap-64";
  }

  let WrapperClass = "md:px-28 lg:px-80 xl:px-80 2xl:px-80 3xl:px-0";
  WrapperClass = showSlider ? `pl-24 ${WrapperClass}` : `px-28 ${WrapperClass}`;

  const slidesElement = useMemo(
    () => cards?.map((card: MediaCard, index: number) => (
      <OrionSlide key={index} card={card} />
    )),
    [cards],
  );

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}

      <div
        className={`our-product-container pt-24 pb-24 lg:pt-40 lg:pb-40 2xl:pt-60 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}
        style={overrideContainerStyle}
      >
        <Wrapper clearDefaultStyles className={WrapperClass}>
          {title && (
            <Typography className="mb-36 md:mb-48 text-32 xl:text-32 2xl:text-48 font-medium leading-8 xl:leading-[2.25rem] 2xl:leading-[3.25rem] text-[#090909]" as="h3">
              {title}
            </Typography>
          )}
          {description && <Subtitle className="mb-24" subtitle={description} />}
          {!showSlider ? (
            <div className={`flex w-full gap-24 ${overrideSlideGap}`}>{slidesElement}</div>
          ) : (
            <div className="relative w-full">
              <Slider
                dots={false}
                solidArrows
                autoplay={{
                  delay: 5000,
                  disableOnInteraction: false,
                }}
                arrows={false}
                allowArrowsOnTouchDevice
                allowTouchMove={isTouchDevice}
                slideWidth={290}
                nextButtonStyles={{ transform: 'translateY(-150%)', right: '10px' }}
                prevButtonStyles={{ transform: 'translateY(-150%)', left: '10px' }}
                spaceBetween={16}
              >
                {slidesElement}
              </Slider>
            </div>
          )}
        </Wrapper>
      </div>
    </>
  );
}
